import {
  DAILY_DEPARTMENTS,
  type DailyDepartment,
  type DailyReportKind,
  type DailyReportStatus,
} from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import type { DailyNoonReport, DailyReportItem, DailyTask, DailyTaskInput } from './types.js';

/** BGM sekmeleri: All, Deck, Engine, Safety, Admin; günde başka departman varsa o da eklenir. */
export const PLAN_TABS: readonly DailyDepartment[] = ['deck', 'engine', 'safety', 'admin'];

export function planTabs(tasks: readonly DailyTask[]): DailyDepartment[] {
  const present = new Set(tasks.map((t) => t.department));
  return DAILY_DEPARTMENTS.filter((d) => PLAN_TABS.includes(d) || (present.has(d) && d !== 'all'));
}

/** Sekme ve arama; "All" departmanlı görev her sekmede görünür (bütün ekip). */
export function filterTasks(
  tasks: readonly DailyTask[],
  filter: { department: DailyDepartment | 'all'; query: string },
): DailyTask[] {
  const q = filter.query.trim().toLocaleLowerCase();
  return tasks.filter(
    (t) =>
      (filter.department === 'all' ||
        t.department === filter.department ||
        t.department === 'all') &&
      (q === '' ||
        [t.description, t.responsible, t.workOrder?.no ?? ''].some((v) =>
          v.toLocaleLowerCase().includes(q),
        )),
  );
}

/** Görevin rozeti: gecikmiş görev "Overdue" (kırmızı), BGM renkleri. */
export function taskBadge(task: Pick<DailyTask, 'status' | 'overdue'>): {
  tone: StatusTone;
  key: string;
} {
  if (task.overdue) return { tone: 'danger', key: 'overdue' };
  switch (task.status) {
    case 'completed':
      return { tone: 'success', key: 'completed' };
    case 'in_progress':
      return { tone: 'warning', key: 'in_progress' };
    case 'cancelled':
      return { tone: 'neutral', key: 'cancelled' };
    default:
      return { tone: 'info', key: 'planned' };
  }
}

export function priorityTone(priority: string): StatusTone {
  return priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'success';
}

/** Raporun rozeti; Defect / Observation gönderildiyse açık/kapalı gösterilir (ortağın taslağı). */
export function reportBadge(item: Pick<DailyReportItem, 'status' | 'kind' | 'open'>): {
  tone: StatusTone;
  key: string;
} {
  const tones: Record<DailyReportStatus, StatusTone> = {
    draft: 'neutral',
    submitted: 'info',
    approved: 'success',
    final: 'info',
    returned: 'danger',
  };
  if (item.kind === 'defect_observation' && item.status !== 'draft' && item.status !== 'returned') {
    return item.open ? { tone: 'warning', key: 'open' } : { tone: 'success', key: 'closed' };
  }
  return { tone: tones[item.status], key: item.status };
}

export function noonBadge(status: DailyNoonReport['status']): { tone: StatusTone; key: string } {
  if (status === 'submitted') return { tone: 'success', key: 'submitted' };
  if (status === 'reopened') return { tone: 'danger', key: 'reopened' };
  return { tone: 'neutral', key: 'draft' };
}

/** Listenin satırı: günlük rapor ya da sefer Noon raporu (salt okunur). */
export type ReportRow =
  | {
      source: 'daily';
      id: string;
      kind: DailyReportKind;
      at: string;
      offset: number;
      item: DailyReportItem;
    }
  | { source: 'noon'; id: string; kind: 'noon'; at: string; offset: number; noon: DailyNoonReport };

export function reportRows(
  items: readonly DailyReportItem[],
  noon: readonly DailyNoonReport[],
): ReportRow[] {
  return [
    ...items.map(
      (item): ReportRow => ({
        source: 'daily',
        id: item.id,
        kind: item.kind,
        at: item.reportedAt,
        offset: item.tzOffsetMin,
        item,
      }),
    ),
    ...noon.map(
      (n): ReportRow => ({
        source: 'noon',
        id: n.id,
        kind: 'noon',
        at: n.reportedAt,
        offset: n.tzOffsetMin,
        noon: n,
      }),
    ),
  ];
}

export type ReportSortKey = 'at' | 'kind' | 'summary' | 'status' | 'submittedBy';
export interface ReportSort {
  key: ReportSortKey;
  dir: 'asc' | 'desc';
}

export function rowSummary(row: ReportRow): string {
  if (row.source === 'daily') return row.item.summary;
  return row.noon.condition ?? '';
}

export function rowStatus(row: ReportRow): string {
  return row.source === 'daily' ? reportBadge(row.item).key : noonBadge(row.noon.status).key;
}

export function filterRows(
  rows: readonly ReportRow[],
  filter: { kind: DailyReportKind | 'noon' | 'all'; query: string },
  names: Readonly<Record<string, string>>,
): ReportRow[] {
  const q = filter.query.trim().toLocaleLowerCase();
  return rows.filter((row) => {
    if (filter.kind !== 'all' && row.kind !== filter.kind) return false;
    if (q === '') return true;
    const by = row.source === 'daily' ? (names[row.item.submittedBy ?? ''] ?? '') : '';
    return [rowSummary(row), by, row.source === 'noon' ? row.noon.voyageNo : ''].some((v) =>
      v.toLocaleLowerCase().includes(q),
    );
  });
}

export function sortRows(
  rows: readonly ReportRow[],
  sort: ReportSort,
  names: Readonly<Record<string, string>>,
): ReportRow[] {
  const value = (row: ReportRow): string => {
    switch (sort.key) {
      case 'kind':
        return row.kind;
      case 'summary':
        return rowSummary(row);
      case 'status':
        return rowStatus(row);
      case 'submittedBy':
        return row.source === 'daily'
          ? (names[row.item.submittedBy ?? ''] ?? '')
          : (row.noon.submittedBy ?? '');
      default:
        return row.at;
    }
  };
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort(
    (a, b) => factor * (value(a).localeCompare(value(b)) || a.at.localeCompare(b.at)),
  );
}

export function pageOf<T>(rows: readonly T[], page: number, size: number) {
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const current = Math.min(Math.max(1, page), pages);
  return { pages, page: current, rows: rows.slice((current - 1) * size, current * size) };
}

/** Tür kartları (ortağın taslağı): günün son raporu, Defect / Observation'da açık kalem sayısı. */
export const CARD_KINDS = ['noon', 'daily', 'defect_observation', 'safety', 'port'] as const;
export type CardKind = (typeof CARD_KINDS)[number];

export function kindCards(rows: readonly ReportRow[]) {
  return CARD_KINDS.map((kind) => {
    const ofKind = rows.filter((r) => r.kind === kind).sort((a, b) => b.at.localeCompare(a.at));
    const openItems = ofKind.filter((r) => r.source === 'daily' && r.item.open).length;
    return { kind, latest: ofKind[0] ?? null, count: ofKind.length, openItems };
  });
}

/** Grafiğin ölçeği: en büyük değere yuvarlanmış çift sayı; en az 8 (BGM ekseni 0–8). */
export function chartScale(max: number): { top: number; ticks: number[] } {
  const top = Math.max(8, Math.ceil(max / 2) * 2);
  const step = top / 4;
  return { top, ticks: [0, step, step * 2, step * 3, top] };
}

export const CHART_KINDS = ['daily', 'port', 'defect_observation', 'safety'] as const;

/** Halka: tamamlanan oranı (0–1). */
export function ringRatio(done: number, total: number): number {
  return total === 0 ? 0 : Math.min(1, done / total);
}

export function emptyTask(day: string, department: DailyDepartment = 'deck'): DailyTaskInput {
  return {
    planDay: day,
    startTime: '08:00',
    endTime: '09:00',
    department,
    description: '',
    responsible: '',
    priority: 'medium',
    workOrderId: null,
    permitRequired: false,
    riskNote: null,
    sparesTools: null,
  };
}

export function taskInputOf(task: DailyTask): DailyTaskInput {
  return {
    planDay: task.planDay,
    startTime: task.startTime,
    endTime: task.endTime,
    department: task.department,
    description: task.description,
    responsible: task.responsible,
    priority: task.priority,
    workOrderId: task.workOrder?.id ?? null,
    permitRequired: task.permitRequired,
    riskNote: task.riskNote,
    sparesTools: task.sparesTools,
  };
}

/** Raporun türe göre alanları (giriş formu ve ayrıntı paneli aynı listeyi kullanır). */
export type FieldSpec =
  | { key: string; type: 'text' | 'textarea'; required?: boolean }
  | { key: string; type: 'select'; options: readonly string[]; labels: string; required?: boolean }
  | { key: string; type: 'day' }
  | { key: string; type: 'switch' };

export const REPORT_FIELDS: Readonly<Record<DailyReportKind, readonly FieldSpec[]>> = {
  daily: [
    {
      key: 'weather',
      type: 'select',
      options: ['good', 'fair', 'moderate', 'poor', 'bad'],
      labels: 'weather',
      required: true,
    },
    { key: 'position', type: 'text', required: true },
    {
      key: 'seaCondition',
      type: 'select',
      options: ['calm', 'slight', 'moderate', 'rough', 'very_rough'],
      labels: 'sea',
      required: true,
    },
    { key: 'nextPort', type: 'text' },
  ],
  port: [
    { key: 'port', type: 'text', required: true },
    { key: 'berth', type: 'text' },
    { key: 'activities', type: 'textarea' },
    { key: 'remarks', type: 'textarea' },
  ],
  defect_observation: [
    {
      key: 'category',
      type: 'select',
      options: ['defect', 'observation'],
      labels: 'categories',
      required: true,
    },
    { key: 'location', type: 'text', required: true },
    { key: 'assignedTo', type: 'text' },
    { key: 'deadline', type: 'day' },
    { key: 'actionTaken', type: 'textarea' },
    { key: 'closed', type: 'switch' },
  ],
  safety: [
    { key: 'topic', type: 'text', required: true },
    {
      key: 'result',
      type: 'select',
      options: ['passed', 'partial', 'failed'],
      labels: 'results',
      required: true,
    },
    { key: 'nextDue', type: 'day' },
    { key: 'actionTaken', type: 'textarea' },
    { key: 'remarks', type: 'textarea' },
  ],
};

/** Daily raporunun bölüm sekmeleri (BGM "Daily Report Entry"). */
export const DAILY_SECTIONS = ['deck', 'engine', 'safety', 'environment', 'remarks'] as const;

/** Form alanının etiket anahtarı (`daily.form.<anahtar>`). */
export function fieldLabelKey(key: string): string {
  if (key === 'seaCondition') return 'sea';
  if (key === 'position') return 'position';
  return key;
}
